import { useEffect, useRef, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

const projects = [
  {
    name: 'AutoServis Premium',
    category: 'Firemný web',
    result: '+190% nových zákazníkov',
    image: 'https://readdy.ai/api/search-image?query=modern%20automotive%20service%20website%20design%20displayed%20on%20laptop%20screen%20clean%20professional%20dark%20theme%20with%20orange%20accents%20car%20service%20booking%20interface%20minimal%20elegant%20UI%20design%20premium%20feel&width=800&height=560&seq=tw-proj-01&orientation=landscape',
    color: 'from-orange-500/20 to-amber-500/10',
  },
  {
    name: 'NutriLife',
    category: 'Prezentačný web',
    result: '+65% online rezervácií',
    image: 'https://readdy.ai/api/search-image?query=beautiful%20health%20nutrition%20wellness%20website%20design%20on%20laptop%20screen%20clean%20white%20minimal%20aesthetic%20green%20accents%20product%20showcase%20modern%20typography%20elegant%20layout%20premium%20web%20design&width=800&height=560&seq=tw-proj-02&orientation=landscape',
    color: 'from-emerald-500/20 to-teal-500/10',
  },
  {
    name: 'KráľovskáSpa',
    category: 'Rezervačný web',
    result: '+280% organická návštevnosť',
    image: 'https://readdy.ai/api/search-image?query=luxury%20spa%20wellness%20center%20website%20design%20displayed%20on%20laptop%20elegant%20cream%20beige%20tones%20sophisticated%20typography%20booking%20system%20interface%20premium%20beauty%20brand%20minimal%20clean%20aesthetic&width=800&height=560&seq=tw-proj-03&orientation=landscape',
    color: 'from-rose-500/20 to-pink-500/10',
  },
];

interface TWShowcaseProps {
  onCtaClick: () => void;
}

export default function TWShowcase({ onCtaClick }: TWShowcaseProps) {
  const { ref, inView } = useInView();

  return (
    <section id="ukazky" className="py-24 bg-zinc-950">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref}>
          <div
            className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-gallery-line text-zinc-300 text-sm" />
              <span className="text-zinc-300 text-sm font-semibold">Naše práce</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight">
              Weby, ktoré priniesli reálne výsledky
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed">
              Každý projekt je iný — ale výsledok je vždy rovnaký: viac zákazníkov, viac dôvery, viac predaja.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {projects.map((project, i) => (
              <div
                key={project.name}
                className={`group rounded-2xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all duration-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="relative w-full h-52 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  <div className="absolute top-3 left-3">
                    <span className="bg-zinc-950/80 backdrop-blur-sm text-zinc-300 text-xs font-semibold px-3 py-1.5 rounded-full border border-zinc-700/60">
                      {project.category}
                    </span>
                  </div>
                </div>
                <div className="p-5 bg-zinc-900">
                  <h3 className="text-white font-bold text-base mb-2">{project.name}</h3>
                  <div className="flex items-center gap-2">
                    <i className="ri-arrow-up-line text-emerald-400 text-sm" />
                    <span className="text-emerald-400 text-sm font-semibold">{project.result}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className={`text-center transition-all duration-700 delay-400 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <button
              onClick={onCtaClick}
              className="inline-flex items-center gap-2.5 bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Chcem podobný web pre moju firmu
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
